
import Page from '@/Page.vue';
import { Component } from 'vue-property-decorator';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import {
    getFilterById,
    saveNewFilter,
    updateFilter,
    deleteFilter,
    setSelectedFilterLocal,
    clearSelectedFilterLocal,
    unpackSettings,
    listSavedFilters,
} from '@/libs/tpm-analytics/core/saved-filters';
import LocationSelect from '@/libs/common/components-smart/LocationSelect.vue';
import WorkplaceSelect from '@/libs/common/components-smart/WorkplaceSelect.vue';
import ProjectSelect from '@/libs/common/components-smart/ProjectSelect.vue';
import ShiftTypeSelect from '@/libs/common/components-smart/ShiftTypeSelect.vue';
import ShiftTeamTypeSelect from '@/libs/common/components-smart/ShiftTeamTypeSelect.vue';
import FaultTypeSelect from '@/libs/common/components-smart/FaultTypeSelect.vue';
import DatePicker from '@/libs/common/components/DatePicker.vue';

@Component({
    components: {
        LocationSelect,
        WorkplaceSelect,
        ProjectSelect,
        ShiftTypeSelect,
        ShiftTeamTypeSelect,
        FaultTypeSelect,
        DatePicker,
    },
})
export default class TpmAnalytics extends Page {
    savedFilters: { id: string; name: string; isPublic?: boolean; settings?: any }[] = [];
    selectedSavedFilterId: string | null = null;
    manageDialogVisible = false;
    manageLoading = false;
    filterSearch = '';
    menuOpenId: string | null = null;
    saveMenuOpen = false;
    editDialogVisible = false;
    editName = '';
    editPublic = false;
    overwriteDialogVisible = false;
    overwriteTarget: { id: string; name: string } | null = null;
    deleteDialogVisible = false;
    deleteTarget: { id: string; name: string } | null = null;
    saveDialogVisible = false;
    saveFilterName = '';
    saveFilterPublic = false;
    saveMode: 'existing' | 'new' = 'new';
    snackbar = { visible: false, text: '' } as any;
    filterSummaries: { [id: string]: string } = {};
    syncingFromQuery = false;
    showAllFiltersDialog = false;
    dialogFilterSearch = '';
    maxVisibleFilters = 12; // Dynamicky počítáno podle šířky
    resizeObserver: any = null; // ResizeObserver pro adaptivní layout
    resizeDebounceTimer: any = null; // Debounce pro window resize
    // Full edit dialog state
    editFullDialogVisible = false;
    editFullId: string | null = null;
    editFullName = '';
    editFullState: any = {
        periodPreset: 'last7d',
        customFrom: null,
        customTo: null,
        granularity: 'day',
        selectedLocationIds: [],
        selectedWorkplaceIds: [],
        selectedProjectIds: [],
        selectedShiftTypeIds: [],
        selectedShiftTeamTypeIds: [],
        selectedFaultTypeIds: [],
    };
    get tenantId(): string | null {
        return authStore.useGetter(AuthGetter.GetTenant);
    }

    get maintenanceBadgeCount(): number {
        // Placeholder badge to mirror design. Replace when API available.
        return 3;
    }

    get visibleFilters(): Array<{ id: string; name: string; isPublic?: boolean; settings?: any }> {
        return this.savedFilters.slice(0, this.maxVisibleFilters);
    }

    get hasMoreFilters(): boolean {
        return this.savedFilters.length > this.maxVisibleFilters;
    }

    get hiddenFiltersCount(): number {
        return Math.max(0, this.savedFilters.length - this.maxVisibleFilters);
    }

    get isActiveFilterHidden(): boolean {
        if (!this.selectedSavedFilterId) return false;
        const visibleIds = this.visibleFilters.map((f) => f.id);
        return !visibleIds.includes(this.selectedSavedFilterId);
    }

    get filteredDialogFilters(): Array<{ id: string; name: string; isPublic?: boolean; settings?: any }> {
        if (!this.dialogFilterSearch || this.dialogFilterSearch.trim() === '') {
            return this.savedFilters;
        }
        const search = this.dialogFilterSearch.toLowerCase().trim();
        return this.savedFilters.filter((f) => f.name.toLowerCase().includes(search));
    }

    async tpmSaveOrUpdateFilter(
        name: string,
        state: any,
        opts?: { targetId?: string | null; isPublic?: boolean }
    ): Promise<string | null> {
        const targetId = opts?.targetId ?? null;
        const isPublic = !!opts?.isPublic;
        if (targetId) {
            const model = await getFilterById(targetId);
            if (!model) return null;
            (model as any).name = name;
            (model as any).isPublic = isPublic;
            const updated = await updateFilter(model as any, state);
            if ((updated as any)?.id) setSelectedFilterLocal((updated as any).id);
            return (updated as any)?.id ?? null;
        }
        const created = await saveNewFilter(name, state, isPublic);
        if (created?.id) setSelectedFilterLocal(created.id);
        return created?.id ?? null;
    }

    async tpmOverwriteFilter(targetId: string, state: any): Promise<string | null> {
        const model = await getFilterById(targetId);
        if (!model) return null;
        const updated = await updateFilter(model as any, state);
        if ((updated as any)?.id) setSelectedFilterLocal((updated as any).id);
        return (updated as any)?.id ?? null;
    }

    async tpmDeleteFilter(id: string): Promise<void> {
        await deleteFilter(id);
        const localId = ((): string | null => {
            try {
                const raw = localStorage.getItem('tpm.analytics.selectedFilter');
                return raw ? JSON.parse(raw || '{}')?.id ?? null : null;
            } catch {
                return null;
            }
        })();
        if (localId === id) clearSelectedFilterLocal();
    }

    created() {
        (this.$root as any).$tpmSaveOrUpdateFilter = this.tpmSaveOrUpdateFilter.bind(this);
        (this.$root as any).$tpmOverwriteFilter = this.tpmOverwriteFilter.bind(this);
        (this.$root as any).$tpmDeleteFilter = this.tpmDeleteFilter.bind(this);
        (this.$root as any).$tpmOnStateChanged = this.onChildStateChanged.bind(this);
        // Seed initial state from URL query: prefer explicit filter values over saved filter id
        const q: any = (this.$route && this.$route.query) || {};
        const sid = (q.sid as any) ? String(q.sid) : null;
        const stParts = this.stateFromQuery(q);
        const stLegacy = this.decodeState((q.st as any) ? String(q.st) : null);
        const hasParts = this.hasAnyFilterParams(q) && Object.keys(stParts || {}).length > 0;
        const st = hasParts ? stParts : stLegacy;
        const hasSt = st && Object.keys(st || {}).length > 0;
        (this.$root as any).$tpmInitialState = hasSt ? st : {};
        (this.$root as any).$tpmInitialSid = sid;
        if (sid) this.selectedSavedFilterId = sid;
        this.loadSavedFilters();
    }

    mounted() {
        // Watch for external URL query changes
        // Při změně URL znovu nastavíme initial state (child si ho aplikuje sám v created())
        // DŮLEŽITÉ: Nepoužíváme fullPath (spouští se i při route change mezi taby)
        //           a přidáváme immediate: false, aby se watch nespustil okamžitě
        let previousQuery = JSON.stringify(this.$route?.query || {});
        this.$watch(
            () => this.$route && JSON.stringify(this.$route.query || {}),
            (newQuery) => {
                // Spustit jen když se skutečně změnily query parametry (ne při tab switchingu)
                if (newQuery !== previousQuery) {
                    previousQuery = newQuery;
                    this.applyFromQueryOnce();
                }
            },
            { immediate: false }
        );

        // Dynamické měření filtrů pro 2 řádky
        this.$nextTick(() => {
            this.calculateVisibleFilters();
            this.setupResizeObserver();
        });

        // Window resize listener jako fallback
        window.addEventListener('resize', this.handleWindowResize);

        // Watch změn v savedFilters pro přepočet
        this.$watch(
            'savedFilters',
            () => {
                this.$nextTick(() => {
                    this.calculateVisibleFilters();
                });
            },
            { deep: true }
        );

        // Reset search při zavření dialogu
        this.$watch('showAllFiltersDialog', (isOpen) => {
            if (!isOpen) {
                this.dialogFilterSearch = '';
            }
        });
    }

    destroyed() {
        if ((this.$root as any).$tpmSaveOrUpdateFilter === this.tpmSaveOrUpdateFilter)
            delete (this.$root as any).$tpmSaveOrUpdateFilter;
        if ((this.$root as any).$tpmOverwriteFilter === this.tpmOverwriteFilter) delete (this.$root as any).$tpmOverwriteFilter;
        if ((this.$root as any).$tpmDeleteFilter === this.tpmDeleteFilter) delete (this.$root as any).$tpmDeleteFilter;
        if ((this.$root as any).$tpmOnStateChanged === this.onChildStateChanged) delete (this.$root as any).$tpmOnStateChanged;

        // Cleanup ResizeObserver
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        // Cleanup window resize listener
        window.removeEventListener('resize', this.handleWindowResize);

        // Cleanup debounce timer
        if (this.resizeDebounceTimer) {
            clearTimeout(this.resizeDebounceTimer);
            this.resizeDebounceTimer = null;
        }
    }

    get savedFilterItems() {
        return this.savedFilters.map((f) => ({ text: f.name, value: f.id }));
    }
    get filteredSavedFilters() {
        const q = (this.filterSearch || '').toString().toLowerCase();
        if (!q) return this.savedFilters;
        return this.savedFilters.filter((f) => (f.name || '').toLowerCase().indexOf(q) >= 0);
    }
    get activeFilterName(): string | null {
        const id = this.selectedSavedFilterId;
        if (!id) return null;
        const f = this.savedFilters.find((x) => x.id === id);
        return f?.name || null;
    }
    get shownCountLabel(): string {
        return String(
            this.$t('tpmFilters.shownCountFmt', { shown: this.filteredSavedFilters.length, total: this.savedFilters.length })
        );
    }
    get activeCountLabel(): string {
        const c = this.selectedSavedFilterId ? 1 : 0;
        return String(this.$t('tpmFilters.activeCountFmt', { count: c }));
    }
    get fullPeriodItems() {
        return [
            { text: this.$t('tpmFilters.previousDay') as any, value: 'previousDay' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.last7d') as any, value: 'last7d' },
            { text: this.$t('tpmFilters.last14d') as any, value: 'last14d' },
            { text: this.$t('tpmFilters.last30d') as any, value: 'last30d' },
            { text: this.$t('tpmFilters.last3m') as any, value: 'last3m' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.thisWeek') as any, value: 'thisWeek' },
            { text: this.$t('tpmFilters.lastWeek') as any, value: 'lastWeek' },
            { text: this.$t('tpmFilters.thisMonth') as any, value: 'thisMonth' },
            { text: this.$t('tpmFilters.lastMonth') as any, value: 'lastMonth' },
            { text: this.$t('tpmFilters.thisQuarter') as any, value: 'thisQuarter' },
            { text: this.$t('tpmFilters.lastQuarter') as any, value: 'lastQuarter' },
            { text: this.$t('tpmFilters.thisYear') as any, value: 'thisYear' },
            { text: this.$t('tpmFilters.lastYear') as any, value: 'lastYear' },
            { divider: true } as any,
            { text: this.$t('tpmFilters.presetCustom') as any, value: 'custom' },
        ];
    }
    get fullGranularityItems() {
        return [
            { text: this.$t('tpmAnalytics.granularityDay') as any, value: 'day' },
            { text: this.$t('tpmAnalytics.granularityWeek') as any, value: 'week' },
            { text: this.$t('tpmAnalytics.granularityMonth') as any, value: 'month' },
        ];
    }
    async loadSavedFilters() {
        const list = await listSavedFilters();
        this.savedFilters = (list || [])
            .map(
                (x) =>
                    ({
                        id: x.id as string,
                        name: x.name || '—',
                        isPublic: !!(x as any).isPublic,
                        settings: (x as any).settings,
                    } as any)
            )
            .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', 'cs', { sensitivity: 'base' }));
        this.filterSummaries = {};
    }
    getChildCurrentState(): any {
        const fn = (this.$root as any).$tpmGetCurrentState;
        try {
            return typeof fn === 'function' ? fn() : {};
        } catch {
            return {};
        }
    }
    applyChildState(state: any) {
        const fn = (this.$root as any).$tpmApplyState;
        if (typeof fn === 'function') fn(state);
    }
    async onSavedFilterSelected() {
        if (!this.selectedSavedFilterId) {
            clearSelectedFilterLocal();
            return;
        }
        setSelectedFilterLocal(this.selectedSavedFilterId);
        const model = await getFilterById(this.selectedSavedFilterId);
        const state = unpackSettings(model?.settings);
        this.applyChildState(state);
        this.updateQuery({ sid: this.selectedSavedFilterId, st: state });
    }
    openSaveDialog() {
        const prefill = this.savedFilters.find((f) => f.id === this.selectedSavedFilterId)?.name || '';
        this.saveFilterName = prefill;
        this.saveFilterPublic = !!(this.savedFilters.find((f) => f.id === this.selectedSavedFilterId) as any)?.isPublic;
        this.saveMode = this.selectedSavedFilterId ? 'existing' : 'new';
        this.saveDialogVisible = true;
    }
    onSaveToExisting() {
        this.saveMenuOpen = false;
        const f = this.savedFilters.find((x) => x.id === this.selectedSavedFilterId);
        if (f) this.confirmOverwriteFilter(f);
    }
    onSaveAsNew() {
        this.saveMenuOpen = false;
        this.openCreateFilter();
    }
    async confirmSaveCurrentFilter() {
        const name = (this.saveFilterName || '').trim();
        if (!name) return;
        const state = this.getChildCurrentState();
        if (this.saveMode === 'existing' && this.selectedSavedFilterId) {
            const id = await (this.$root as any).$tpmSaveOrUpdateFilter(name, state, {
                targetId: this.selectedSavedFilterId,
                isPublic: this.saveFilterPublic,
            });
            if (id) {
                setSelectedFilterLocal(id);
                this.selectedSavedFilterId = id;
                this.toast(this.$t('tpmFilters.updatedToast') as any);
                this.updateQuery({ sid: id, st: state });
            }
        } else {
            const id = await (this.$root as any).$tpmSaveOrUpdateFilter(name, state, { isPublic: this.saveFilterPublic });
            if (id) {
                await this.loadSavedFilters();
                setSelectedFilterLocal(id);
                this.selectedSavedFilterId = id;
                this.toast(this.$t('tpmFilters.savedToast') as any);
                this.updateQuery({ sid: id, st: state });
            }
        }
        await this.loadSavedFilters();
        this.saveDialogVisible = false;
    }
    openManageFilters() {
        this.manageDialogVisible = true;
        this.manageLoading = false;
        this.filterSearch = '';
    }
    openCreateFilter() {
        this.editFullId = null;
        this.editFullName = '';
        const st: any = this.normalizeGranularity(this.getChildCurrentState() || {});
        this.editFullState = {
            periodPreset: st.periodPreset || 'last7d',
            customFrom: st.customFrom || null,
            customTo: st.customTo || null,
            granularity: st.granularity || 'day',
            selectedLocationIds: Array.isArray(st.selectedLocationIds) ? st.selectedLocationIds : [],
            selectedWorkplaceIds: Array.isArray(st.selectedWorkplaceIds) ? st.selectedWorkplaceIds : [],
            selectedProjectIds: Array.isArray(st.selectedProjectIds) ? st.selectedProjectIds : [],
            selectedShiftTypeIds: Array.isArray(st.selectedShiftTypeIds) ? st.selectedShiftTypeIds : [],
            selectedShiftTeamTypeIds: Array.isArray(st.selectedShiftTeamTypeIds) ? st.selectedShiftTeamTypeIds : [],
            selectedFaultTypeIds: Array.isArray(st.selectedFaultTypeIds) ? st.selectedFaultTypeIds : [],
        };
        this.editFullDialogVisible = true;
    }
    openFullEdit(f: any) {
        this.editFullId = f.id;
        this.editFullName = f?.name || '';
        getFilterById(f.id).then((model) => {
            const st = this.normalizeGranularity(unpackSettings(model?.settings) || {});
            this.editFullState = {
                periodPreset: st.periodPreset || 'last7d',
                customFrom: st.customFrom || null,
                customTo: st.customTo || null,
                granularity: st.granularity || 'day',
                selectedLocationIds: Array.isArray(st.selectedLocationIds) ? st.selectedLocationIds : [],
                selectedWorkplaceIds: Array.isArray(st.selectedWorkplaceIds) ? st.selectedWorkplaceIds : [],
                selectedProjectIds: Array.isArray(st.selectedProjectIds) ? st.selectedProjectIds : [],
                selectedShiftTypeIds: Array.isArray(st.selectedShiftTypeIds) ? st.selectedShiftTypeIds : [],
                selectedShiftTeamTypeIds: Array.isArray(st.selectedShiftTeamTypeIds) ? st.selectedShiftTeamTypeIds : [],
                selectedFaultTypeIds: Array.isArray(st.selectedFaultTypeIds) ? st.selectedFaultTypeIds : [],
            };
            this.editFullDialogVisible = true;
        });
    }
    applyFullToCurrent() {
        const full = this.expandGranularity(this.editFullState);
        this.applyChildState(full);
    }
    onMenuInput(id: string, value: boolean) {
        this.menuOpenId = value ? id : null;
    }
    // URL query sync helpers
    private getQueryKeys() {
        return {
            periodPreset: 'period',
            customFrom: 'from',
            customTo: 'to',
            selectedLocationIds: 'loc',
            selectedWorkplaceIds: 'wp',
            selectedProjectIds: 'prj',
            selectedShiftTypeIds: 'stype',
            selectedShiftTeamTypeIds: 'ttype',
            selectedFaultTypeIds: 'ft',
            granularity: 'gran',
        } as const;
    }
    private splitCsv(v: any): string[] {
        if (v == null) return [];
        const s = Array.isArray(v) ? v.join(',') : String(v);
        return s
            .split(',')
            .map((x) => x.trim())
            .filter((x) => !!x);
    }
    private stateFromQuery(q: any): any {
        const keys = this.getQueryKeys();
        const st: any = {};
        const period = q[keys.periodPreset];
        if (period) st.periodPreset = String(period);
        const from = q[keys.customFrom];
        if (from) st.customFrom = String(from);
        const to = q[keys.customTo];
        if (to) st.customTo = String(to);
        const loc = this.splitCsv(q[keys.selectedLocationIds]);
        if (loc.length) st.selectedLocationIds = loc;
        const wp = this.splitCsv(q[keys.selectedWorkplaceIds]);
        if (wp.length) st.selectedWorkplaceIds = wp;
        const prj = this.splitCsv(q[keys.selectedProjectIds]);
        if (prj.length) st.selectedProjectIds = prj;
        const stype = this.splitCsv(q[keys.selectedShiftTypeIds]);
        if (stype.length) st.selectedShiftTypeIds = stype;
        const ttype = this.splitCsv(q[keys.selectedShiftTeamTypeIds]);
        if (ttype.length) st.selectedShiftTeamTypeIds = ttype;
        const ft = this.splitCsv(q[keys.selectedFaultTypeIds]);
        if (ft.length) st.selectedFaultTypeIds = ft;
        const gran = q[keys.granularity];
        if (gran) st.granularity = String(gran);
        return this.normalizeGranularity(st);
    }
    private queryFromState(state: any): any {
        const keys = this.getQueryKeys();
        const st = this.normalizeGranularity(state || {});
        const q: any = {};
        if (st.periodPreset) q[keys.periodPreset] = st.periodPreset;
        if (st.customFrom) q[keys.customFrom] = st.customFrom;
        if (st.customTo) q[keys.customTo] = st.customTo;
        const join = (arr: any) => (Array.isArray(arr) && arr.length ? arr.join(',') : undefined);
        const loc = join(st.selectedLocationIds);
        if (loc) q[keys.selectedLocationIds] = loc;
        const wp = join(st.selectedWorkplaceIds);
        if (wp) q[keys.selectedWorkplaceIds] = wp;
        const prj = join(st.selectedProjectIds);
        if (prj) q[keys.selectedProjectIds] = prj;
        const stype = join(st.selectedShiftTypeIds);
        if (stype) q[keys.selectedShiftTypeIds] = stype;
        const ttype = join(st.selectedShiftTeamTypeIds);
        if (ttype) q[keys.selectedShiftTeamTypeIds] = ttype;
        const ft = join(st.selectedFaultTypeIds);
        if (ft) q[keys.selectedFaultTypeIds] = ft;
        const gran =
            st.granularity || st.oeeGranularity || st.fiGranularity || st.scrapGranularity || st.maintenanceGranularity;
        if (gran) q[keys.granularity] = gran;
        return q;
    }
    private hasAnyFilterParams(q: any): boolean {
        const keys = Object.values(this.getQueryKeys());
        return keys.some((k) => q && typeof q[k] !== 'undefined');
    }
    expandGranularity(state: any): any {
        const s = { ...(state || {}) } as any;
        const gran =
            s.granularity || s.oeeGranularity || s.fiGranularity || s.scrapGranularity || s.maintenanceGranularity || 'day';
        s.granularity = gran;
        s.oeeGranularity = gran;
        s.fiGranularity = gran;
        s.scrapGranularity = gran;
        s.maintenanceGranularity = gran;
        return s;
    }
    normalizeGranularity(state: any): any {
        const s = { ...(state || {}) } as any;
        const gran =
            s.oeeGranularity || s.fiGranularity || s.scrapGranularity || s.maintenanceGranularity || s.granularity || 'day';
        s.granularity = gran;
        if (!s.oeeGranularity) s.oeeGranularity = gran;
        if (!s.fiGranularity) s.fiGranularity = gran;
        if (!s.scrapGranularity) s.scrapGranularity = gran;
        if (!s.maintenanceGranularity) s.maintenanceGranularity = gran;
        return s;
    }
    encodeState(state: any): string {
        try {
            const s = this.normalizeGranularity(state);
            return encodeURIComponent(JSON.stringify(s || {}));
        } catch {
            return '';
        }
    }
    decodeState(s: string | null): any {
        if (!s) return {};
        try {
            const st = JSON.parse(decodeURIComponent(s));
            return this.normalizeGranularity(st);
        } catch {
            return {};
        }
    }
    updateQuery(_next: { sid?: string | null; st?: any | null }) {
        if (this.syncingFromQuery) return;
        const current: any = { ...(this.$route?.query || {}) };
        const keys = this.getQueryKeys();
        // Handle sid if explicitly provided
        if (typeof _next.sid !== 'undefined') {
            if (_next.sid) current.sid = _next.sid as any;
            else delete current.sid;
        }
        // Handle state if explicitly provided
        if (typeof _next.st !== 'undefined') {
            // Always remove legacy combined state param when we update from state
            delete current.st;
            const stRaw = _next.st || {};
            const st = this.normalizeGranularity(stRaw);
            // Determine which fields were explicitly provided by the caller
            const present: { [field: string]: boolean } = {};
            present.periodPreset = Object.prototype.hasOwnProperty.call(stRaw, 'periodPreset');
            present.customFrom = Object.prototype.hasOwnProperty.call(stRaw, 'customFrom');
            present.customTo = Object.prototype.hasOwnProperty.call(stRaw, 'customTo');
            present.selectedLocationIds = Object.prototype.hasOwnProperty.call(stRaw, 'selectedLocationIds');
            present.selectedWorkplaceIds = Object.prototype.hasOwnProperty.call(stRaw, 'selectedWorkplaceIds');
            present.selectedProjectIds = Object.prototype.hasOwnProperty.call(stRaw, 'selectedProjectIds');
            present.selectedShiftTypeIds = Object.prototype.hasOwnProperty.call(stRaw, 'selectedShiftTypeIds');
            present.selectedShiftTeamTypeIds = Object.prototype.hasOwnProperty.call(stRaw, 'selectedShiftTeamTypeIds');
            present.selectedFaultTypeIds = Object.prototype.hasOwnProperty.call(stRaw, 'selectedFaultTypeIds');
            // Granularity can be provided via specific keys as well
            present.granularity =
                Object.prototype.hasOwnProperty.call(stRaw, 'granularity') ||
                Object.prototype.hasOwnProperty.call(stRaw, 'oeeGranularity') ||
                Object.prototype.hasOwnProperty.call(stRaw, 'fiGranularity') ||
                Object.prototype.hasOwnProperty.call(stRaw, 'scrapGranularity') ||
                Object.prototype.hasOwnProperty.call(stRaw, 'maintenanceGranularity');

            // Apply updates per field; if field explicitly present and empty → delete; if not present → preserve
            const applyArray = (field: string, key: string) => {
                if (!present[field]) return; // preserve existing if not provided
                const arr = Array.isArray((st as any)[field]) ? ((st as any)[field] as any[]) : [];
                const v = arr.filter((x) => x != null && String(x).trim().length > 0).join(',');
                if (v.length > 0) current[key] = v as any;
                else delete current[key];
            };
            const applyScalar = (field: string, key: string) => {
                if (!present[field]) return; // preserve existing if not provided
                const val = (st as any)[field];
                if (val != null && String(val).length > 0) current[key] = String(val) as any;
                else delete current[key];
            };

            applyScalar('periodPreset', keys.periodPreset);
            applyScalar('customFrom', keys.customFrom);
            applyScalar('customTo', keys.customTo);
            applyArray('selectedLocationIds', keys.selectedLocationIds);
            applyArray('selectedWorkplaceIds', keys.selectedWorkplaceIds);
            applyArray('selectedProjectIds', keys.selectedProjectIds);
            applyArray('selectedShiftTypeIds', keys.selectedShiftTypeIds);
            applyArray('selectedShiftTeamTypeIds', keys.selectedShiftTeamTypeIds);
            applyArray('selectedFaultTypeIds', keys.selectedFaultTypeIds);
            applyScalar('granularity', keys.granularity);
        }
        const same = JSON.stringify({ ...(this.$route?.query || {}) }) === JSON.stringify(current);
        if (same) return;
        this.$router.replace({ query: current }).catch(() => {
            return undefined;
        });
    }
    onTabChanged() {
        // If URL contains explicit filter params, keep them (do not override with saved filter)
        const hasExplicit = this.hasAnyFilterParams(this.$route?.query || {});
        if (hasExplicit) return;
        const id = this.selectedSavedFilterId;
        if (!id) return;
        getFilterById(id).then((model) => {
            const st = this.normalizeGranularity(unpackSettings(model?.settings) || {});
            this.applyChildState(st);
            // Ensure URL query reflects full state (including gran) immediately on tab switch
            this.updateQuery({ sid: id, st });
        });
    }
    onChildStateChanged() {
        // Child reports filter change – update URL query with latest state; keep current sid if set
        const state = this.getChildCurrentState();
        this.updateQuery({ sid: this.selectedSavedFilterId, st: state });
    }
    async applyFromQueryOnce(): Promise<boolean> {
        if (!this.$route) return false;
        const q: any = this.$route.query || {};
        const sid = q.sid ? String(q.sid) : null;
        const stParts = this.stateFromQuery(q);
        const stLegacy = this.decodeState(q.st ? String(q.st) : null);
        const hasParts = this.hasAnyFilterParams(q) && Object.keys(stParts || {}).length > 0;
        const st = hasParts ? stParts : stLegacy;
        const hasSt = st && Object.keys(st || {}).length > 0;
        if (!sid && !hasSt) return false;
        // Prevent feedback loop while applying
        this.syncingFromQuery = true;
        try {
            // Pouze nastavíme initial state/sid, child komponenty si to aplikují samy v created()
            // DŮVOD: Předejdeme duplikátnímu volání load() - parent by neměl volat child.applyFilterState()
            (this.$root as any).$tpmInitialState = hasSt ? st : {};
            (this.$root as any).$tpmInitialSid = sid;
            if (sid) this.selectedSavedFilterId = sid;

            // REMOVED: Parent už NEVOLÁ fn(st) - child si to aplikuje sám v created()
            // const fn = (this.$root as any).$tpmApplyState;
            // if (hasSt && typeof fn === 'function') fn(st);
            // else if (!hasSt && sid) { ... }

            return true;
        } catch {
            return false;
        } finally {
            this.syncingFromQuery = false;
        }
    }
    // REMOVED: applyFromQueryWhenReady() už není potřeba
    // Aplikujeme stav přímo v created() před tím, než child běží svůj created()
    async confirmFullEdit() {
        const name = (this.editFullName || '').trim();
        if (!name) return;
        if (!this.editFullId) {
            const full = this.expandGranularity(this.editFullState);
            const id = await (this.$root as any).$tpmSaveOrUpdateFilter(name, full, { isPublic: false });
            if (id) {
                await this.loadSavedFilters();
                setSelectedFilterLocal(id);
                this.selectedSavedFilterId = id;
                this.toast(this.$t('tpmFilters.savedToast') as any);
                this.updateQuery({ sid: id, st: full });
            }
            this.editFullDialogVisible = false;
            return;
        }
        const model = await getFilterById(this.editFullId);
        if (!model) {
            this.editFullDialogVisible = false;
            return;
        }
        const isPublic = !!(model as any).isPublic;
        const full = this.expandGranularity(this.editFullState);
        await (this.$root as any).$tpmSaveOrUpdateFilter(name, full, {
            targetId: this.editFullId,
            isPublic,
        });
        await this.loadSavedFilters();
        this.editFullDialogVisible = false;
        this.toast(this.$t('tpmFilters.updatedToast') as any);
        this.updateQuery({ sid: this.editFullId, st: full });
    }
    async confirmEditDialog() {
        const name = (this.editName || '').trim();
        if (!name) return;
        const state = this.getChildCurrentState();
        const id = await (this.$root as any).$tpmSaveOrUpdateFilter(name, state, { isPublic: this.editPublic });
        if (id) {
            await this.loadSavedFilters();
            setSelectedFilterLocal(id);
            this.selectedSavedFilterId = id;
            this.toast(this.$t('tpmFilters.savedToast') as any);
        }
        this.editDialogVisible = false;
    }
    applyFilter(id: string) {
        // Toggle: clicking the active filter clears selection and resets filters
        if (this.selectedSavedFilterId === id) {
            this.selectedSavedFilterId = null;
            clearSelectedFilterLocal();
            const empty = {
                periodPreset: 'last7d',
                customFrom: null,
                customTo: null,
                selectedLocationIds: [],
                selectedWorkplaceIds: [],
                selectedProjectIds: [],
                selectedShiftTypeIds: [],
                selectedShiftTeamTypeIds: [],
            } as any;
            this.applyChildState(empty);
            this.updateQuery({ sid: null, st: empty });
            return;
        }
        this.selectedSavedFilterId = id;
        this.onSavedFilterSelected();
        this.manageDialogVisible = false;
    }

    applyFilterAndClose(id: string) {
        this.applyFilter(id);
        this.showAllFiltersDialog = false;
        this.dialogFilterSearch = '';
    }

    calculateVisibleFilters() {
        if (this.savedFilters.length === 0) {
            this.maxVisibleFilters = this.savedFilters.length;
            return;
        }

        const measurementEl = this.$refs.filtersMeasurement as HTMLElement;
        if (!measurementEl) {
            this.maxVisibleFilters = this.savedFilters.length;
            return;
        }

        // Počkáme na vykreslení
        this.$nextTick(() => {
            const chips = measurementEl.querySelectorAll('.filter-chip');
            if (chips.length === 0) {
                this.maxVisibleFilters = this.savedFilters.length;
                return;
            }

            // Změříme reálné pozice čipů po vykreslení
            const containerTop = measurementEl.offsetTop;
            const maxHeight = 80; // Max 2 řádky (32px + 8px gap + 32px + 8px padding)
            const maxBottom = containerTop + maxHeight;

            let visibleCount = 0;
            const chipPositions: Array<{ index: number; top: number; bottom: number; left: number; width: number }> = [];

            // Zjistíme pozice všech čipů
            for (let i = 0; i < chips.length; i++) {
                const chip = chips[i] as HTMLElement;
                const rect = chip.getBoundingClientRect();
                const parentRect = measurementEl.getBoundingClientRect();
                const relativeTop = rect.top - parentRect.top;
                const relativeBottom = relativeTop + rect.height;

                chipPositions.push({
                    index: i,
                    top: relativeTop,
                    bottom: relativeBottom,
                    left: rect.left - parentRect.left,
                    width: rect.width,
                });

                // Pokud je čip stále v rámci max výšky, započítáme ho
                if (relativeBottom <= maxHeight) {
                    visibleCount = i + 1;
                } else {
                    break;
                }
            }

            // Pokud máme více filtrů, než se vejde, musíme rezervovat místo pro tlačítko
            if (visibleCount < this.savedFilters.length && visibleCount > 0) {
                const lastVisibleChip = chipPositions[visibleCount - 1];
                const buttonWidth = 100; // Odhadovaná šířka tlačítka "+X další"
                const gap = 8;
                const containerWidth = measurementEl.offsetWidth;

                // Zjistíme, jestli se tlačítko vejde na stejný řádek jako poslední viditelný čip
                const lastChipRight = lastVisibleChip.left + lastVisibleChip.width;
                const buttonRight = lastChipRight + gap + buttonWidth;

                if (buttonRight > containerWidth) {
                    // Tlačítko by přeteklo na další řádek, musíme odebrat čipy
                    // Najdeme poslední čip, který se vejde včetně tlačítka
                    for (let i = visibleCount - 1; i >= 0; i--) {
                        const chip = chipPositions[i];
                        const chipRight = chip.left + chip.width;
                        const testButtonRight = chipRight + gap + buttonWidth;

                        // Zkontrolujeme, jestli by se čip + tlačítko vešly na řádek
                        // a zároveň jestli by celá výška nepřekročila limit
                        if (testButtonRight <= containerWidth && chip.bottom <= maxHeight) {
                            visibleCount = i + 1;
                            break;
                        }
                    }
                }
            }

            // Pokud se nevejdou žádné filtry, zobrazíme aspoň jeden
            this.maxVisibleFilters = Math.max(1, Math.min(visibleCount, this.savedFilters.length));
        });
    }

    setupResizeObserver() {
        const container = this.$refs.filtersContainer as HTMLElement;
        if (!container) return;

        // ResizeObserver pro adaptivní přepočet při změně šířky
        if (typeof (window as any).ResizeObserver !== 'undefined') {
            this.resizeObserver = new (window as any).ResizeObserver(() => {
                this.calculateVisibleFilters();
            });
            this.resizeObserver.observe(container);
        }
    }

    handleWindowResize() {
        // Debounce pro window resize - přepočítá se až 200ms po ukončení změny velikosti
        if (this.resizeDebounceTimer) {
            clearTimeout(this.resizeDebounceTimer);
        }
        this.resizeDebounceTimer = setTimeout(() => {
            this.calculateVisibleFilters();
        }, 200);
    }

    confirmOverwriteFilter(f: any) {
        this.menuOpenId = null;
        // Open full edit prefilled with CURRENT child filters; keep name unchanged
        this.editFullId = f.id;
        this.editFullName = f?.name || '';
        const st: any = this.normalizeGranularity(this.getChildCurrentState() || {});
        this.editFullState = {
            periodPreset: st.periodPreset || 'last7d',
            customFrom: st.customFrom || null,
            customTo: st.customTo || null,
            granularity: st.granularity || 'day',
            selectedLocationIds: Array.isArray(st.selectedLocationIds) ? st.selectedLocationIds : [],
            selectedWorkplaceIds: Array.isArray(st.selectedWorkplaceIds) ? st.selectedWorkplaceIds : [],
            selectedProjectIds: Array.isArray(st.selectedProjectIds) ? st.selectedProjectIds : [],
            selectedShiftTypeIds: Array.isArray(st.selectedShiftTypeIds) ? st.selectedShiftTypeIds : [],
            selectedShiftTeamTypeIds: Array.isArray(st.selectedShiftTeamTypeIds) ? st.selectedShiftTeamTypeIds : [],
            selectedFaultTypeIds: Array.isArray(st.selectedFaultTypeIds) ? st.selectedFaultTypeIds : [],
        };
        this.editFullDialogVisible = true;
    }
    async doOverwriteFilter() {
        if (!this.overwriteTarget) return;
        const state = this.getChildCurrentState();
        await (this.$root as any).$tpmOverwriteFilter(this.overwriteTarget.id, state);
        await this.loadSavedFilters();
        this.toast(this.$t('tpmFilters.overwrittenToast') as any);
        this.overwriteDialogVisible = false;
        this.updateQuery({ sid: this.overwriteTarget.id, st: state });
    }
    confirmDeleteFilter(f: any) {
        this.menuOpenId = null;
        this.deleteTarget = f;
        this.deleteDialogVisible = true;
    }
    async doDeleteFilter() {
        if (!this.deleteTarget) return;
        this.manageLoading = true;
        await (this.$root as any).$tpmDeleteFilter(this.deleteTarget!.id as string);
        if (this.selectedSavedFilterId === this.deleteTarget.id) {
            this.selectedSavedFilterId = null;
        }
        await this.loadSavedFilters();
        this.manageLoading = false;
        this.deleteDialogVisible = false;
        this.toast(this.$t('tpmFilters.deletedToast') as any);
    }
    confirmDeleteCurrent() {
        if (this.selectedSavedFilterId) {
            this.confirmDeleteFilter({
                id: this.selectedSavedFilterId,
                name: this.savedFilters.find((f) => f.id === this.selectedSavedFilterId)?.name || '',
            });
        }
    }
    toast(text: string) {
        this.snackbar.text = text;
        this.snackbar.visible = true;
    }

    async computeFilterSummaries() {
        try {
            const tasks = this.savedFilters.map(async (f) => {
                const model = await getFilterById(f.id);
                if (!model) return;
                const state: any = unpackSettings(model.settings);
                const line = this.buildSummaryLine(state);
                this.$set(this.filterSummaries, f.id, line);
            });
            await Promise.all(tasks);
        } catch {
            /* noop */
        }
    }
    buildSummaryLine(state: any): string {
        if (!state) return '';
        const parts: string[] = [];
        // Period label
        const periodLabel = this.periodPresetToLabel(state.periodPreset);
        if (periodLabel) parts.push(periodLabel);
        // Helper to push label with count if any
        const pushCount = (labelKey: string, arr: any) => {
            var count = Array.isArray(arr) ? arr.length : 0;
            if (count > 0) {
                parts.push(String(this.$t(labelKey)) + ': ' + count);
            }
        };
        pushCount('audit.plan.location', state.selectedLocationIds);
        pushCount('audit.plan.workplace', state.selectedWorkplaceIds);
        pushCount('audit.plan.project', state.selectedProjectIds);
        pushCount('audit.plan.shiftType', state.selectedShiftTypeIds);
        pushCount('audit.plan.shiftTeamType', state.selectedShiftTeamTypeIds);
        pushCount('tpmAnalytics.faultType', state.selectedFaultTypeIds);
        var line = parts.join(' • ');
        if (line.length > 200) {
            line = line.slice(0, 200) + '...';
        }
        return line;
    }
    getFilterSummary(f: any): string {
        const id = f?.id as string;
        if (!id) return '';
        const existing = this.filterSummaries[id];
        if (existing) return existing;
        const state: any = unpackSettings((f as any)?.settings);
        const line = this.buildSummaryLine(state);
        this.$set(this.filterSummaries, id, line);
        return line;
    }
    periodPresetToLabel(preset: string | undefined): string {
        switch (preset) {
            case 'previousDay':
                return String(this.$t('tpmFilters.previousDay'));
            case 'last7d':
                return String(this.$t('tpmFilters.last7d'));
            case 'last14d':
                return String(this.$t('tpmFilters.last14d'));
            case 'last30d':
                return String(this.$t('tpmFilters.last30d'));
            case 'last3m':
                return String(this.$t('tpmFilters.last3m'));
            case 'thisWeek':
                return String(this.$t('tpmFilters.thisWeek'));
            case 'lastWeek':
                return String(this.$t('tpmFilters.lastWeek'));
            case 'thisMonth':
                return String(this.$t('tpmFilters.thisMonth'));
            case 'lastMonth':
                return String(this.$t('tpmFilters.lastMonth'));
            case 'thisQuarter':
                return String(this.$t('tpmFilters.thisQuarter'));
            case 'lastQuarter':
                return String(this.$t('tpmFilters.lastQuarter'));
            case 'thisYear':
                return String(this.$t('tpmFilters.thisYear'));
            case 'lastYear':
                return String(this.$t('tpmFilters.lastYear'));
            case 'custom':
                return `${this.$t('common.dateSince')}…`;
            default:
                return '';
        }
    }
}
